import { Icon } from '@shh/ui';
import { useEffect, useState } from 'react';
import { eventStates } from '../data/agenda';
import { useClock } from '../data/clock';
import { eventsOn, involves } from '../data/derive';
import { useFamily } from '../data/family';
import { useWeather } from '../data/weather';
import { todayText } from '../i18n/today';
import { useLens } from './lens';

/** Vertical scroll position, updated at most once per frame. */
export function useScrollY(): number {
  const [y, setY] = useState(() => (typeof window === 'undefined' ? 0 : window.scrollY));
  useEffect(() => {
    let frame = 0;
    const on = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => setY(window.scrollY));
    };
    window.addEventListener('scroll', on, { passive: true });
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('scroll', on);
    };
  }, []);
  return y;
}

/**
 * What docks into the header once the big clock of "Heute" has scrolled away (Mike, 02.10.),
 * by available room: tablets and the wall the time and the weather, wide screens also the next
 * event; phones nothing (their status bar shows the time). Everything else stays on the page –
 * the header must stay calm.
 */
export function HeaderGlance() {
  const f = useFamily();
  const lens = useLens();
  const now = useClock(f.timeZone);
  const weather = useWeather(f.household?.location, f.timeZone);
  const today = eventsOn(f, f.today).filter(
    (e) => e.start && (lens.id === 'all' || involves(e, lens.id)),
  );
  const states = eventStates(today, now);
  const next = today.find((e) => states.get(e.id) === 'next');
  return (
    // phones: nothing docks – title and buttons need the room
    <div className="flex min-w-0 items-center gap-gap-md text-text-secondary max-sm:hidden">
      <span className="font-display text-lg font-black text-text-primary tabular-nums">{now}</span>
      {weather && (
        <span className="flex items-center gap-gap-xs font-semibold">
          <Icon icon={weather.icon} size="md" weight="duotone" />
          {weather.temp}°
        </span>
      )}
      {next && (
        <span className="min-w-0 truncate text-sm max-xl:hidden">
          {todayText.upNextAt(next.start ?? '')}{' '}
          <strong className="text-text-primary">{next.title}</strong>
        </span>
      )}
    </div>
  );
}
